import { ActionCreator } from 'redux';
import { ThunkAction } from 'redux-thunk';

import uniqBy from 'lodash/uniqBy';
import { CommentCountResponse } from '@/interface/controller/commentCountController/CommentCountController';
import { sessionStorage } from '@/shared/sessionstorage';
import { localStorage } from '@/shared/localstorage';
import {
  PersonalizedArticleItem,
  PersonalizedArticleResponse
} from '@/interface/controller/personalizedArticleController/PersonalizedArticleController';
import {
  ArticleExpertReactionInfo,
  ExpertRecommendArticles
} from '@/domain/model/articleReaction/IArticleReactionRepository';
import { ArticleType, getUserPropertyId } from '@/public/template/common/comment/types';

/**
 * SessionStorage用のKEY
 */
// Feedデータ
const KEY_SESSION_FEED = 'YAHOO.JP.News.personalizedFeed.PC';
// DISLIKEに登録した記事リスト情報
const KEY_SESSION_DISLIKE = 'YAHOO.JP.News.Top.dislike.idList.PC';
/**
 *  LocalStorage用のKEY,version
 */
// コメント表示/非表示
const LOCALSTORAGE_KEY_COMMENT = 'YAHOO.JP.News.commentPlugin';
const LOCALSTORAGE_VER_COMMENT = 1;

// DISLIKEに登録した記事リスト情報
const LOCALSTORAGE_KEY_DISLIKE_LIST = 'YAHOO.JP.News.Top.dislike.idList';
const LOCALSTORAGE_VER_DISLIKE_LIST = 1;
/**
 * 主要のタイムライン最大表示数
 * https://wiki.workers-hub.com/pages/viewpage.action?pageId=1423835166
 */
const MAX_SHOW_PERSONALIZED_FEED: number = 124;
const DISPLAY_ITEMS_PER_PAGE = [42, 50, 32];

const actionTypes = {
  FETCHING_FEEDS: 'personalizedFeed/FETCHING_FEEDS',
  FETCHING_FEEDS_SUCCESS: 'personalizedFeed/FETCHING_FEEDS_SUCCESS',
  FETCHING_FEEDS_FAILURE: 'personalizedFeed/FETCHING_FEEDS_FAILURE',

  FETCHED_COMMENT_COUNT: 'personalizedFeed/FETCHED_COMMENT_COUNT',
  FETCH_COMMENT_COUNT_STATUS: 'personalizedFeed/FETCH_COMMENT_COUNT_STATUS',

  FETCHED_EXPERT_RECOMMEND_COUNT: 'articlesOtherData/FETCHED_EXPERT_RECOMMEND_COUNT',
  FETCH_EXPERT_RECOMMEND_COUNT_STATUS: 'articlesOtherData/FETCH_EXPERT_RECOMMEND_COUNT_STATUS',

  FETCHED_EXPERT_RECOMMEND_ARTICLES: 'personalizedFeed/FETCHED_EXPERT_RECOMMEND_ARTICLES',

  RESTORE_CONFIG_FROM_STORAGE: 'personalizedFeed/RESTORE_CONFIG_FROM_STORAGE',
  REFLECT_TO_TIMELINE: 'personalizedFeed/REFLECT_TO_TIMELINE',

  ADD_DISLIKE: 'personalizedFeed/ADD_DISLIKE',
  REMOVE_DISLIKE: 'personalizedFeed/REMOVE_DISLIKE',

  SET_OPEN_DISLIKE_POPUP: 'newsFeed/SET_OPEN_DISLIKE_POPUP'
} as const;

export type FetchStatus = 'init' | 'fetching' | 'complete';

/**
 * タイムラインの初回取得
 *
 * @param csrfToken
 */
const fetchInitialFeed: ActionCreator<
  ThunkAction<Promise<void>, PersonalizedFeedPageCommonState, void, PersonalizedFeedAction>
> = (csrfToken: string) => {
  return async dispatch => {
    const stateToRestore = sessionStorage.get<PersonalizedFeedState>(KEY_SESSION_FEED);
    const navigation = window?.performance?.getEntriesByType('navigation');

    if (stateToRestore && navigation?.[0]?.type === 'back_forward') {
      // ブラウザバック時はセッションストレージから状態を復元する
      dispatch(reflectToTimeline(stateToRestore));
      return;
    }

    sessionStorage.remove(KEY_SESSION_FEED);
    sessionStorage.remove(KEY_SESSION_DISLIKE);

    dispatch(fetchFeed(csrfToken));
  };
};

/**
 * タイムラインの記事を取得する
 *
 * @param csrfToken
 */
const fetchFeed: ActionCreator<
  ThunkAction<Promise<void>, PersonalizedFeedPageCommonState, void, PersonalizedFeedAction>
> = (csrfToken: string) => {
  return async (dispatch, getState) => {
    dispatch(fetchingFeeds());

    const FETCH_FEED_COUNT = 50;
    const currentPage = getState().personalizedFeed.currentPage;
    return fetch(`/api/personalized-articles?results=${FETCH_FEED_COUNT}&page=${currentPage + 1}`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { UserToken: csrfToken }
    })
      .then(res => {
        if (res.ok) {
          return res.json();
        }
        return Promise.reject({ statusCode: res.status });
      })
      .then((body: PersonalizedArticleResponse) => {
        dispatch(fetchingFeedsSuccess(body));
      })
      .catch(_error => {
        dispatch(fetchingFeedsFailure());
      });
  };
};

/**
 * 記事のコメント数を取得する
 *
 * @param contentId
 * @param articleType
 */
const fetchCommentCount: ActionCreator<
  ThunkAction<Promise<void>, PersonalizedFeedPageCommonState, void, PersonalizedFeedAction>
> = (contentId: string, articleType: ArticleType | null) => {
  return async dispatch => {
    // articleTypeがnullの場合はpropertyIdの区別ができないため無視する
    if (articleType === null) {
      return;
    }
    dispatch(updateCommentCountFetchStatus(contentId, 'fetching'));

    const propertyId = getUserPropertyId(articleType);
    return fetch(`/api/public/comment-count/properties/${propertyId}/articles/${contentId}`, {
      method: 'GET',
      credentials: 'same-origin'
    })
      .then(res => {
        if (res.ok) {
          return res.json();
        }
        return Promise.reject({ statusCode: res.status });
      })
      .then((body: CommentCountResponse) => {
        dispatch(fetchedCommentCount(contentId, body.count));
      })
      .finally(() => {
        dispatch(updateCommentCountFetchStatus(contentId, 'complete'));
      });
  };
};

/**
 * 記事の推薦数を取得する
 *
 * @param contentId
 */
const fetchExpertRecommendCount: ActionCreator<
  ThunkAction<Promise<void>, PersonalizedFeedPageCommonState, void, PersonalizedFeedAction>
> = (contentId: string) => {
  return async dispatch => {
    dispatch(updateExpertRecommendCountFetchStatus(contentId, 'fetching'));

    return fetch(`/api/public/article-expert-reaction/${contentId}`)
      .then(async res => {
        if (res.ok) {
          return res.json();
        }
        return Promise.reject({ statusCode: res.status });
      })
      .then((body: ArticleExpertReactionInfo) => {
        dispatch(fetchedExpertRecommendCount(contentId, body.count));
      })
      .finally(() => {
        dispatch(updateExpertRecommendCountFetchStatus(contentId, 'complete'));
      });
  };
};

/**
 * エキスパート推薦記事情報を取得する
 */
const fetchExpertRecommendArticles: ActionCreator<
  ThunkAction<Promise<void>, PersonalizedFeedPageCommonState, void, PersonalizedFeedAction>
> = () => {
  return async (dispatch, getState) => {
    const state = getState();
    if (state.personalizedFeed.isFetchedExpertRecommendArticles) {
      // 取得済みの場合、各記事の専門家推薦情報の更新だけ行い、情報は取得しない
      dispatch(fetchedExpertRecommendArticles(state.personalizedFeed.expertRecommendArticles));
      return;
    }

    return fetch('/api/public/expert-recommend-articles', {
      method: 'GET',
      credentials: 'same-origin'
    })
      .then(async res => {
        if (res.ok) {
          return res.json();
        }
        return Promise.reject({ statusCode: res.status });
      })
      .then((expertRecommendArticles: ExpertRecommendArticles) => {
        dispatch(fetchedExpertRecommendArticles(expertRecommendArticles));
      })
      .catch(_error => {
        dispatch(fetchedExpertRecommendArticles({ count: 0, articles: [] }));
      });
  };
};

const fetchingFeeds = () => {
  return {
    type: actionTypes.FETCHING_FEEDS
  };
};

const fetchingFeedsSuccess = (response: PersonalizedArticleResponse) => {
  return {
    type: actionTypes.FETCHING_FEEDS_SUCCESS,
    payload: {
      articles: response.items,
      experiments: response.experiments
    }
  };
};

const fetchingFeedsFailure = () => {
  return {
    type: actionTypes.FETCHING_FEEDS_FAILURE
  };
};

const fetchedCommentCount = (contentId: string, count: number) => {
  return {
    type: actionTypes.FETCHED_COMMENT_COUNT,
    payload: { contentId, count }
  };
};

const fetchedExpertRecommendCount = (contentId: string, count: number) => {
  return {
    type: actionTypes.FETCHED_EXPERT_RECOMMEND_COUNT,
    payload: { contentId, count }
  };
};

const fetchedExpertRecommendArticles = (expertRecommendArticles: ExpertRecommendArticles) => {
  return {
    type: actionTypes.FETCHED_EXPERT_RECOMMEND_ARTICLES,
    payload: { expertRecommendArticles }
  };
};

const updateCommentCountFetchStatus = (contentId: string, fetchStatus: FetchStatus) => {
  return {
    type: actionTypes.FETCH_COMMENT_COUNT_STATUS,
    payload: {
      contentId,
      fetchStatus
    }
  };
};

const updateExpertRecommendCountFetchStatus = (contentId: string, fetchStatus: FetchStatus) => {
  return {
    type: actionTypes.FETCH_EXPERT_RECOMMEND_COUNT_STATUS,
    payload: {
      contentId,
      fetchStatus
    }
  };
};

const reflectToTimeline = (state: PersonalizedFeedState) => {
  return {
    type: actionTypes.REFLECT_TO_TIMELINE,
    payload: { state }
  };
};

const restoreConfigFromStorage = () => {
  return {
    type: actionTypes.RESTORE_CONFIG_FROM_STORAGE
  };
};

const addDislike = (contentId: string) => {
  return {
    type: actionTypes.ADD_DISLIKE,
    payload: { contentId }
  };
};

const removeDislike = (contentId: string) => {
  return {
    type: actionTypes.REMOVE_DISLIKE,
    payload: { contentId }
  };
};

const setIsOpenDislikePopUp = (payload: { contentId: string; isOpenDislikePopUp: boolean }) => ({
  type: actionTypes.SET_OPEN_DISLIKE_POPUP,
  payload
});

type PersonalizedFeedAction = ReturnType<
  | typeof fetchingFeeds
  | typeof fetchingFeedsSuccess
  | typeof fetchingFeedsFailure
  | typeof fetchedCommentCount
  | typeof updateCommentCountFetchStatus
  | typeof fetchedExpertRecommendCount
  | typeof updateExpertRecommendCountFetchStatus
  | typeof fetchedExpertRecommendArticles
  | typeof restoreConfigFromStorage
  | typeof reflectToTimeline
  | typeof addDislike
  | typeof removeDislike
  | typeof setIsOpenDislikePopUp
>;

export const actions = {
  fetchInitialFeed,
  fetchFeed,
  fetchCommentCount,
  fetchExpertRecommendCount,
  fetchExpertRecommendArticles,
  restoreConfigFromStorage,
  addDislike,
  removeDislike,
  setIsOpenDislikePopUp
} as const;

export interface NewsFeedContentItem {
  readonly article: PersonalizedArticleItem;
  readonly comment: {
    readonly fetchStatus: FetchStatus;
    readonly commentCount?: number;
  };
  readonly expertReaction: {
    readonly fetchStatus: FetchStatus;
    readonly expertRecommendCount?: number;
  };
  // dislike設定時の完了状態
  readonly isDislikeModified: boolean;
  readonly isOpenDislikePopUp: boolean;
}

export interface PersonalizedFeedPageCommonState {
  readonly personalizedFeed: PersonalizedFeedState;
}

export interface PersonalizedFeedState {
  readonly displayItems: Array<NewsFeedContentItem>;
  readonly bufferItems: Array<NewsFeedContentItem>;
  readonly currentPage: number;
  readonly isError: boolean;
  readonly isLimit: boolean;
  readonly experiments: Array<NewsFeedExperiment>;
  readonly isShowComment: boolean; // ユーザー設定のコメント非表示管理フラグ
  readonly isFetching: boolean;
  readonly expertRecommendArticles: ExpertRecommendArticles;
  readonly isFetchedExpertRecommendArticles: boolean;
}

const initialState: PersonalizedFeedState = {
  displayItems: [],
  bufferItems: [],
  experiments: [],
  currentPage: 0,
  isError: false,
  isLimit: false,
  isShowComment: true,
  isFetching: false,
  expertRecommendArticles: { count: 0, articles: [] },
  isFetchedExpertRecommendArticles: false
};

interface CommentPluginStorage {
  readonly isShow: boolean;
}

interface DislikeIdList {
  readonly items: Array<DislikeIdItem>;
}
interface DislikeIdItem {
  readonly shcid: string;
}

interface NewsFeedExperiment {
  readonly experimentId: string;
  readonly bucketId: string;
}

export default (state: PersonalizedFeedState = initialState, action: PersonalizedFeedAction): PersonalizedFeedState => {
  switch (action.type) {
    /** タイムラインの追加読み込み中の状態にする */
    case actionTypes.FETCHING_FEEDS: {
      return { ...state, isFetching: true, isError: false };
    }

    /** タイムラインを追加読み込み成功時の結果を反映する */
    case actionTypes.FETCHING_FEEDS_SUCCESS: {
      // ローカルストレージのdislikeリストを取得する
      const rawLocalStorageDislikeList = localStorage.get<DislikeIdList>(
        LOCALSTORAGE_KEY_DISLIKE_LIST,
        LOCALSTORAGE_VER_DISLIKE_LIST
      );

      const localStorageDislikeList = rawLocalStorageDislikeList?.items ?? [];

      const additionalData: {
        comment: {
          fetchStatus: FetchStatus;
        };
        expertReaction: {
          fetchStatus: FetchStatus;
        };
      } = {
        comment: {
          fetchStatus: 'init'
        },
        expertReaction: {
          fetchStatus: 'init'
        }
      };

      const newContents = action.payload.articles
        .filter((article: PersonalizedArticleItem) => {
          // dislikeリストに登録されていた場合除去する
          return !localStorageDislikeList.some(({ shcid }) => shcid === article.contentId);
        })
        .map((article: PersonalizedArticleItem) => {
          // 取得したコンテンツを表示用に初期化する
          return {
            article,
            ...additionalData,
            isDislikeModified: false,
            isOpenDislikePopUp: false
          };
        });

      let newDisplayItems: NewsFeedContentItem[];
      let newBufferItems: NewsFeedContentItem[];

      if (state.currentPage < DISPLAY_ITEMS_PER_PAGE.length) {
        // APIから取得できる記事数の単位と一度に表示する記事数の要件が異なるためバッファを持って表示する件数を調節する
        const tmpBufferItems = [...state.bufferItems, ...newContents];

        newDisplayItems = tmpBufferItems.slice(0, DISPLAY_ITEMS_PER_PAGE[state.currentPage]);
        newBufferItems = tmpBufferItems.slice(DISPLAY_ITEMS_PER_PAGE[state.currentPage]);
      } else {
        // 一度に表示する記事数が定義されていないページの場合は新規で取得した記事を全て表示する（主に開発環境向け）
        newDisplayItems = newContents;
        newBufferItems = [];
      }

      const isLimit =
        newContents.length === 0 || state.displayItems.length + newDisplayItems.length >= MAX_SHOW_PERSONALIZED_FEED;

      const updateState: PersonalizedFeedState = {
        ...state,
        displayItems: state.displayItems.concat(newDisplayItems),
        bufferItems: newBufferItems,
        experiments: uniqBy(state.experiments.concat(action.payload.experiments), exp => exp.experimentId),
        currentPage: state.currentPage + 1,
        isFetching: false,
        isError: false,
        isLimit
      };

      // セッションストレージの内容を更新
      sessionStorage.set(KEY_SESSION_FEED, updateState);

      return updateState;
    }

    /** タイムラインを追加読み込み失敗時の結果を反映する */
    case actionTypes.FETCHING_FEEDS_FAILURE: {
      return { ...state, isFetching: false, isError: true };
    }

    case actionTypes.FETCHED_EXPERT_RECOMMEND_COUNT: {
      const itemsWithUpdatedStatus = state.displayItems.map(item => {
        if (item.article.contentId === action.payload.contentId) {
          return {
            ...item,
            expertReaction: { ...item.expertReaction, expertRecommendCount: action.payload.count ?? 0 }
          };
        }
        return item;
      });

      return {
        ...state,
        displayItems: itemsWithUpdatedStatus
      };
    }

    case actionTypes.FETCH_EXPERT_RECOMMEND_COUNT_STATUS: {
      const itemsWithUpdatedStatus = state.displayItems.map(item => {
        if (item.article.contentId === action.payload.contentId) {
          return {
            ...item,
            expertReaction: { ...item.expertReaction, fetchStatus: action.payload.fetchStatus }
          };
        }
        return item;
      });

      return {
        ...state,
        displayItems: itemsWithUpdatedStatus
      };
    }

    // エキスパート推薦記事情報を取得し、一致する記事の推薦情報を更新する。
    // エキスパート推薦記事情報の取得に失敗した場合も再度読み込み処理は行わないため、isFetchedExpertRecommendArticles を更新する。
    case actionTypes.FETCHED_EXPERT_RECOMMEND_ARTICLES: {
      const itemsWithUpdatedStatus = state.displayItems.map(item => {
        // payload.body.articles の中にある記事IDと一致する記事に対して推薦数を更新する
        const matched = action.payload.expertRecommendArticles.articles.find(
          (article: { contentId: string; reactions: { count: number } }) => article.contentId === item.article.contentId
        );
        if (matched) {
          return {
            ...item,
            expertReaction: {
              ...item.expertReaction,
              expertRecommendCount: matched.reactions.count ?? 0,
              fetchStatus: 'complete' as FetchStatus
            }
          };
        }
        return item;
      });

      const updateState: PersonalizedFeedState = {
        ...state,
        displayItems: itemsWithUpdatedStatus,
        expertRecommendArticles: action.payload.expertRecommendArticles,
        isFetchedExpertRecommendArticles: true
      };

      // セッションストレージの内容を更新
      sessionStorage.set(KEY_SESSION_FEED, updateState);

      return updateState;
    }

    // コメント件数の更新を行う
    case actionTypes.FETCHED_COMMENT_COUNT: {
      const itemsWithUpdatedStatus = state.displayItems.map(item => {
        if (item.article.contentId === action.payload.contentId) {
          return { ...item, comment: { ...item.comment, commentCount: action.payload.count ?? 0 } };
        }
        return item;
      });

      return {
        ...state,
        displayItems: itemsWithUpdatedStatus
      };
    }

    case actionTypes.FETCH_COMMENT_COUNT_STATUS: {
      const itemsWithUpdatedStatus = state.displayItems.map(item => {
        if (item.article.contentId === action.payload.contentId) {
          return {
            ...item,
            comment: { ...item.comment, fetchStatus: action.payload.fetchStatus }
          };
        }
        return item;
      });

      return {
        ...state,
        displayItems: itemsWithUpdatedStatus
      };
    }

    /** ローカルストレージに保存された設定を復元する */
    case actionTypes.RESTORE_CONFIG_FROM_STORAGE: {
      // コメントの表示設定
      const commentPluginStorage = localStorage.get<CommentPluginStorage>(
        LOCALSTORAGE_KEY_COMMENT,
        LOCALSTORAGE_VER_COMMENT
      );
      const isShowComment = commentPluginStorage?.isShow ?? state.isShowComment;

      return {
        ...state,
        isShowComment
      };
    }

    /** セッションストレージに保存されたタイムラインの状態を復元する */
    case actionTypes.REFLECT_TO_TIMELINE: {
      return {
        ...state,
        ...action.payload.state
      };
    }

    /** DISLIKE追加 */
    case actionTypes.ADD_DISLIKE: {
      // ローカルストレージの更新処理
      const rawLocalStorageDislikeList = localStorage.get<DislikeIdList>(
        LOCALSTORAGE_KEY_DISLIKE_LIST,
        LOCALSTORAGE_VER_DISLIKE_LIST
      );

      const localStorageDislikeList = rawLocalStorageDislikeList?.items ?? [];

      // リストに未登録だった場合登録してローカルストレージに保存する
      if (!localStorageDislikeList.some(({ shcid }) => shcid === action.payload.contentId)) {
        localStorageDislikeList.push({ shcid: action.payload.contentId });

        localStorage.set<DislikeIdList>(LOCALSTORAGE_KEY_DISLIKE_LIST, LOCALSTORAGE_VER_DISLIKE_LIST, {
          items: localStorageDislikeList
        });
      }

      // セッションストレージの更新処理
      const rawSessionStorageDislikeList = sessionStorage.get<DislikeIdList>(KEY_SESSION_DISLIKE);
      const sessionStorageDislikeList = rawSessionStorageDislikeList?.items ?? [];

      // リストに未登録だった場合登録してローカルストレージに保存する
      if (!sessionStorageDislikeList.some(({ shcid }) => shcid === action.payload.contentId)) {
        sessionStorageDislikeList.push({ shcid: action.payload.contentId });

        sessionStorage.set<DislikeIdList>(KEY_SESSION_DISLIKE, {
          items: localStorageDislikeList
        });
      }

      // 現タイムラインにdislike追加を反映
      const updatedItems = state.displayItems.map(item => {
        if (item.article.contentId === action.payload.contentId) {
          return { ...item, isDislikeModified: true };
        }
        return item;
      });

      const updatedState: PersonalizedFeedState = { ...state, displayItems: updatedItems };

      // セッションストレージの内容を更新
      sessionStorage.set(KEY_SESSION_FEED, updatedState);

      return updatedState;
    }

    /** DISLIKE削除(元に戻す) */
    case actionTypes.REMOVE_DISLIKE: {
      // ローカルストレージの更新処理
      const rawLocalStorageDislikeList = localStorage.get<DislikeIdList>(
        LOCALSTORAGE_KEY_DISLIKE_LIST,
        LOCALSTORAGE_VER_DISLIKE_LIST
      );

      const localStorageDislikeList = rawLocalStorageDislikeList?.items ?? [];

      // リストを更新してローカルストレージに保存する
      localStorage.set<DislikeIdList>(LOCALSTORAGE_KEY_DISLIKE_LIST, LOCALSTORAGE_VER_DISLIKE_LIST, {
        items: localStorageDislikeList.filter(item => {
          return item.shcid !== action.payload.contentId;
        })
      });

      // セッションストレージの更新処理
      const rawSessionStorageDislikeList = sessionStorage.get<DislikeIdList>(KEY_SESSION_DISLIKE);
      const sessionStorageDislikeList = rawSessionStorageDislikeList?.items ?? [];

      // リストを更新してセッションストレージに保存する
      sessionStorage.set<DislikeIdList>(KEY_SESSION_DISLIKE, {
        items: sessionStorageDislikeList.filter(item => {
          return item.shcid !== action.payload.contentId;
        })
      });

      // 現タイムラインにdislike削除を反映
      const updatedItems = state.displayItems.map(item => {
        if (item.article.contentId === action.payload.contentId) {
          return { ...item, isDislikeModified: false } as const;
        }
        return item;
      });

      const updatedState: PersonalizedFeedState = { ...state, displayItems: updatedItems };

      // セッションストレージの内容を更新
      sessionStorage.set(KEY_SESSION_FEED, updatedState);

      return updatedState;
    }

    case actionTypes.SET_OPEN_DISLIKE_POPUP: {
      const displayItems = state.displayItems.map(item => ({
        ...item,
        isOpenDislikePopUp:
          item.article.contentId === action.payload.contentId ? action.payload.isOpenDislikePopUp : false
      }));
      const bufferItems = state.bufferItems.map(item => ({
        ...item,
        isOpenDislikePopUp:
          item.article.contentId === action.payload.contentId ? action.payload.isOpenDislikePopUp : false
      }));
      return { ...state, displayItems, bufferItems };
    }
    default:
      return state;
  }
};
